import React from 'react';
import './PlotConditions.css';
import { useFarm } from '../../context/FarmContext';
import { useI18n } from '../../context/I18nContext';

// Lookup dictionary for metric units
const UNITS_MAP = {
  humidity_percent: '%',
  dewpoint_c: '°C',
  temperature_c: '°C',
  temperature_celsius: '°C',
  heat_index: '°C',
  precipitation_mm: 'mm',
  distance: 'mm',
  pressure_hpa: 'hPa',
  solar_radiation_wm2: 'W/m²',
  vpd_kpa: 'kPa',
  leaf_wetness_duration_hrs: 'hrs',
  degree_days: 'DD',
  degree_hours: 'DH',
};

// Translation dictionary / label overrides
const LABELS_MAP = {
  humidity_percent: 'Humidity',
  dewpoint_c: 'Dew Point',
  temperature_c: 'Temperature',
  temperature_celsius: 'Temperature',
  precipitation_mm: 'Precipitation',
  pressure_hpa: 'Pressure',
  solar_radiation_wm2: 'Solar Radiation',
  vpd_kpa: 'VPD',
  leaf_wetness_duration_hrs: 'Leaf Wetness',
  degree_days: 'Degree Days',
  degree_hours: 'Degree Hours',
  growth_stage: 'Growth Stage',
  cydia_pomonella: 'Cydia Pomonella (Codling Moth)',
  'malus_domestica:gala': 'Malus Domestica (Gala Apple)'
};

const CARDINAL_DIRECTIONS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
];

// Helper to derive wind speed, angle, and cardinal direction
const calculateWind = (u, v) => {
  if (u == null || v == null) return null;

  const speed = Math.sqrt(u * u + v * v);
  let angleDeg = (Math.atan2(-u, -v) * (180 / Math.PI));
  if (angleDeg < 0) angleDeg += 360;

  const cardinalIdx = Math.round(angleDeg / 22.5) % 16;
  const cardinal = CARDINAL_DIRECTIONS[cardinalIdx];

  return {
    speed: speed.toFixed(2),
    angle: Math.round(angleDeg),
    cardinal
  };
};

// Helper to estimate clear-sky peak solar radiation max
const calculateMaxSolarRadiation = (lat = 45, date = new Date()) => {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  const doy = Math.floor(diff / (1000 * 60 * 60 * 24));

  const latRad = (lat * Math.PI) / 180;
  const declination = 0.409 * Math.sin(((2 * Math.PI) / 365) * doy - 1.39);
  const sunsetAngle = Math.acos(-Math.tan(latRad) * Math.tan(declination));

  const ra = 1367 * (1 + 0.033 * Math.cos(((2 * Math.PI) / 365) * doy));
  const maxInstantaneous = Math.max(200, Math.min(1200, ra * Math.sin(sunsetAngle)));

  return Math.round(maxInstantaneous);
};

// Format values with units
const formatValue = (val, key) => {
  if (val == null || val === '') return 'N/A';

  const unit = UNITS_MAP[key] || '';
  let formattedVal = val;

  if (typeof val === 'number') {
    formattedVal = Number.isInteger(val) ? val.toString() : val.toFixed(2);
  }

  return unit ? `${formattedVal} ${unit}` : `${formattedVal}`;
};

// Humanized key or dictionary lookups
const getLabel = (key) => {
  if (LABELS_MAP[key]) return LABELS_MAP[key];

  const cleaned = key.replace(/_(percent|c|kpa|mm|hpa|wm2|hrs)$/i, '');
  return cleaned
    .replace(/[:_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const PlotConditions = () => {
  const { t } = useI18n();

  const { selectedPlotSummary, selectedPlotConditions, loadingConditions } = useFarm();

  if (loadingConditions) {
    return <div className="conditions-panel panel">Loading plot conditions...</div>;
  }

  if (!selectedPlotConditions && !selectedPlotSummary) {
    return <div className="conditions-panel panel">Select a plot to view conditions.</div>;
  }

  const rawData = selectedPlotConditions?.conditions || selectedPlotConditions || {};
  const plotName = selectedPlotSummary?.name || rawData?.metadata?.name || 'Plot';
  const latitude = selectedPlotSummary?.latitude || rawData?.metadata?.latitude || 45;
  const timestamp = rawData?.timestamp ? new Date(rawData.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;

  const getProperty = (x) => x || {};
  const getSub = (x) => {
    if (x === undefined) return({});
    return Object.entries(x).reduce((acc, [key, obj]) => {
      acc[key] = obj.value;
      return acc;
    }, {});
  };
  const meteo = getProperty(rawData.meteorological);
  const agronomic = { ...getProperty(rawData.agronomic), ...getSub(rawData.unclassified) };
  const models = getProperty(rawData.models);

  //console.log("[PlotConditions] plotConditions:",selectedPlotConditions);

  // Extract transformable keys from standard meteo keys
  const { wind_u, wind_v, solar_radiation_wm2, ...leftMeteoEntries } = meteo;

  // Process wind
  const windInfo = calculateWind(wind_u, wind_v);

  // Process solar potential percentage
  const actualSolar = solar_radiation_wm2 ?? null;
  const maxSolar = calculateMaxSolarRadiation(latitude);
  const solarPercentage = actualSolar != null ? Math.min(100, Math.max(0, Math.round((actualSolar / maxSolar) * 100))) : 0;

  return (
    <div className="conditions-panel panel">
      <div className="panel-header">
        <h3>{t('plotConditions.currentConditions')} - {plotName}</h3>
        {timestamp && <span className="plot-subtitle">Updated at {timestamp}</span>}
      </div>

      {/* Meteorological Section */}
      {Object.keys(meteo).length > 0 && (
        <div className="metrics-section">
          <h4 className="section-title">Meteorological Data</h4>
          <div className="meteo-layout-row">

            {/* Left side: Standard Metric Cards */}
            <div className="metrics-grid meteo-left-grid">
              {Object.entries(leftMeteoEntries).map(([key, val]) => (
                <div className="metric-card" key={key}>
                  <span className="metric-label">{getLabel(key)}</span>
                  <span className="metric-value">{formatValue(val, key)}</span>
                </div>
              ))}
            </div>

            {/* Right side: Transformed Visual Indicator Cards */}
            <div className="meteo-right-visuals">

              {/* Consolidated Wind Card */}
              <div className="metric-card visual-card">
                <span className="metric-label">Wind Direction & Speed</span>
                {windInfo ? (
                  <div className="visual-card-content">
                    <div
                      className="wind-arrow-container"
                      style={{ transform: `rotate(${windInfo.angle}deg)` }}
                      title={`Blowing from ${windInfo.angle}° (${windInfo.cardinal})`}
                    >
                      <svg viewBox="0 0 24 24" className="wind-arrow-icon" fill="currentColor">
                        <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
                      </svg>
                    </div>
                    <div className="visual-details">
                      <span className="metric-value">{windInfo.speed} m/s</span>
                      <span className="sub-value">{windInfo.cardinal} ({windInfo.angle}°)</span>
                    </div>
                  </div>
                ) : (
                  <span className="metric-value">N/A</span>
                )}
              </div>

              {/* Solar Radiation Sun Indicator Card */}
              <div className="metric-card visual-card">
                <span className="metric-label">Solar Radiation</span>
                {actualSolar != null ? (
                  <div className="visual-card-content">
                    <div className="sun-indicator-container" title={`${solarPercentage}% active radiation (${100 - solarPercentage}% shaded)`}>
                      <svg viewBox="0 0 24 24" className="sun-icon">
                        <defs>
                          <linearGradient id={`sunGradient-${solarPercentage}`} x1="0" y1="1" x2="0" y2="0">
                            <stop offset={`${solarPercentage}%`} stopColor="#f59e0b" />
                            <stop offset={`${solarPercentage}%`} stopColor="#374151" />
                          </linearGradient>
                        </defs>
                        <path
                          fill={`url(#sunGradient-${solarPercentage})`}
                          d="M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm-9 4a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2H3zm16 0a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2h-2zm-8-8a1 1 0 1 0-2 0v2a1 1 0 1 0 2 0V3zm0 16a1 1 0 1 0-2 0v2a1 1 0 1 0 2 0v-2zM5.636 4.222a1 1 0 0 0-1.414 1.414l1.414 1.414a1 1 0 0 0 1.414-1.414L5.636 4.222zm12.728 12.728a1 1 0 0 0-1.414 1.414l1.414 1.414a1 1 0 0 0 1.414-1.414l-1.414-1.414zM18.364 5.636a1 1 0 0 0 1.414-1.414l-1.414-1.414a1 1 0 0 0-1.414 1.414l1.414 1.414zM5.636 18.364a1 1 0 0 0 1.414 1.414l-1.414 1.414a1 1 0 0 0-1.414-1.414l1.414-1.414z"
                        />
                      </svg>
                    </div>
                    <div className="visual-details">
                      <span className="metric-value">{actualSolar} W/m²</span>
                      <span className="sub-value">{solarPercentage}% potential (~{maxSolar} W/m²)</span>
                    </div>
                  </div>
                ) : (
                  <span className="metric-value">N/A</span>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Agronomic Metrics */}
      {Object.keys(agronomic).length > 0 && (
        <div className="metrics-section">
          <h4 className="section-title">Agronomic & Real-time Metrics</h4>
          <div className="metrics-grid">
            {Object.entries(agronomic).map(([key, val]) => (
              <div className="metric-card" key={key}>
                <span className="metric-label">{getLabel(key)}</span>
                <span className="metric-value">{formatValue(val, key)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Degree Days & Phenology Models */}
      {Object.keys(models).length > 0 && (
        <div className="metrics-section">
          <h4 className="section-title">Degree Days & Phenology Models</h4>
          <div className="risk-cards-grid">
            {Object.entries(models).map(([modelKey, modelData]) => (
              <div className="risk-detail-card" key={modelKey}>
                <div className="risk-card-header">
                  <span className="risk-title">{getLabel(modelKey)}</span>
                </div>
                <div className="risk-card-body">
                  {Object.entries(modelData || {}).map(([mKey, mVal]) => (
                    <div className="risk-detail-row" key={mKey}>
                      <span className="detail-label">{getLabel(mKey)}</span>
                      <span className="detail-value">{formatValue(mVal, mKey)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlotConditions;
